import React, { useCallback, useEffect, useState } from 'react';
import { isValidMobileNumber } from 'utils/generalUtils';
import MobileInput from 'modules/views/shared/MobileInput';
import { ParsedCountry } from 'react-international-phone';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Button from 'modules/views/shared/Button';
import TextField from 'modules/views/shared/TextField';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import {
  IContact,
  fetchEmailContacts,
  fetchPhoneContacts,
  addContact,
  removeContact,
  makePrimaryContact,
  verifyContact,
} from 'modules/state-mgmt/workerData';
import { fetchWorkerDetails } from 'modules/state-mgmt/onboarding';
import { EnterPasswordModal } from './EnterPasswordModal';
import { RemoveContactModal } from './RemoveContactModal';
import { VerificationSentModal } from './VerificationSentModal';
import { useStyles } from './styles';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type PasswordAction = 'makePrimary' | 'verify' | 'add';

interface IPasswordModalState {
  open: boolean;
  contact: IContact | null;
  action: PasswordAction;
  addContactValue?: string;
  addContactType?: 0 | 1;
}

interface IRemoveModalState {
  open: boolean;
  contact: IContact | null;
}

interface IVerificationSentModalState {
  open: boolean;
  contact: IContact | null;
}

interface ContactSectionProps {
  contactType: 0 | 1;
  primaryContact: IContact | null;
  secondaryContacts: IContact[];
  columnLabel: string;
  noteText: string;
  showAdd: boolean;
  addValue: string;
  addValueError: string;
  // phone only: the full E.164 value used for submission/validation
  addPhoneValue?: string;
  onAddOpen: () => void;
  onAddChange: (v: string) => void;
  onMobileChange?: (data: { phone: string; inputValue: string; country: ParsedCountry }) => void;
  onAddCancel: () => void;
  onAddSubmit: (contactType: 0 | 1, value: string) => void;
  onMakePrimary: (contact: IContact) => void;
  onVerify: (contact: IContact) => void;
  onRemove: (contact: IContact) => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({
  contactType,
  primaryContact,
  secondaryContacts,
  columnLabel,
  noteText,
  showAdd,
  addValue,
  addValueError,
  addPhoneValue,
  onAddOpen,
  onAddChange,
  onMobileChange,
  onAddCancel,
  onAddSubmit,
  onMakePrimary,
  onVerify,
  onRemove,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Automation id prefix — the same ids are used for the desktop table and the mobile card list,
  // which are mutually exclusive by viewport.
  const testIdPrefix = contactType === 0 ? 'login-info-email' : 'login-info-mobile';

  const title =
    contactType === 0 ? t('myProfile.loginInformation.email.title') : t('myProfile.loginInformation.mobile.title');

  const placeholder =
    contactType === 0
      ? t('myProfile.loginInformation.email.placeholder')
      : t('myProfile.loginInformation.mobile.placeholder');

  const emptyText =
    contactType === 0 ? t('myProfile.loginInformation.email.empty') : t('myProfile.loginInformation.mobile.empty');

  const addButtonLabel =
    contactType === 0
      ? t('myProfile.loginInformation.email.addButton')
      : t('myProfile.loginInformation.mobile.addButton');

  const renderMobileCard = (contact: IContact, isPrimary: boolean) => (
    <Box
      key={contact.id}
      className={`${classes.mobileContactCard} ${isPrimary ? classes.mobileContactCardPrimary : classes.mobileContactCardSecondary}`}
      data-testid={isPrimary ? `${testIdPrefix}-primary-row` : `${testIdPrefix}-row-${contact.id}`}
    >
      <Box className={classes.mobileContactCardContent}>
        <span className={classes.mobileContactValue} data-testid={`${testIdPrefix}-value-${contact.id}`}>
          {contact.contactValue}
        </span>
        <Chip
          label={
            isPrimary
              ? t('myProfile.loginInformation.table.primaryBadge')
              : t('myProfile.loginInformation.table.secondaryBadge')
          }
          className={isPrimary ? classes.primaryBadge : classes.secondaryBadge}
        />
      </Box>
      {!isPrimary && (
        <>
          <Divider className={classes.mobileDivider} />
          <Box className={classes.mobileCardActions}>
            {contact.isVerified ? (
              <button
                className={classes.setPrimaryLink}
                data-testid={`${testIdPrefix}-make-primary-button-${contact.id}`}
                onClick={() => onMakePrimary(contact)}
              >
                {t('myProfile.loginInformation.table.makePrimary')}
              </button>
            ) : (
              <button
                className={classes.setPrimaryLink}
                data-testid={`${testIdPrefix}-verify-button-${contact.id}`}
                onClick={() => onVerify(contact)}
              >
                {t('myProfile.loginInformation.table.verify')}
              </button>
            )}
            <button
              className={classes.removeLink}
              data-testid={`${testIdPrefix}-remove-button-${contact.id}`}
              onClick={() => onRemove(contact)}
            >
              {t('myProfile.loginInformation.table.remove')}
            </button>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box
      className={[classes.card, contactType === 0 ? classes.nullBorderTopRadius : ''].join(' ')}
      data-testid={`${testIdPrefix}-section`}
    >
      <Box className={classes.cardHeader}>
        <span className={classes.sectionTitle}>{title}</span>
        {!showAdd &&
          secondaryContacts.length <= 3 &&
          (isMobile ? (
            <IconButton
              className={classes.addIconBtn}
              data-testid={`${testIdPrefix}-add-button`}
              onClick={onAddOpen}
            >
              <AddIcon />
            </IconButton>
          ) : (
            <Button
              label={addButtonLabel}
              startIcon={<AddIcon />}
              styleClass={classes.headerBtn}
              testId={`${testIdPrefix}-add-button`}
              onClick={onAddOpen}
            />
          ))}
      </Box>

      {showAdd && (
        <Box className={classes.addRow}>
          <Box className={classes.addInput}>
            {contactType === 1 ? (
              <MobileInput
                placeholder={placeholder}
                value={addPhoneValue ?? addValue}
                testId={`${testIdPrefix}-add-input`}
                onChange={onMobileChange!}
                error={addValueError}
                fullWidth
              />
            ) : (
              <TextField
                placeholder={placeholder}
                value={addValue}
                testId={`${testIdPrefix}-add-input`}
                onChange={e => onAddChange(e.target.value)}
                error={addValueError}
                fullWidth
              />
            )}
          </Box>
          <Box className={classes.addActions}>
            <Button
              label={t('myProfile.loginInformation.add')}
              styleClass={classes.addBtn}
              testId={`${testIdPrefix}-add-submit-button`}
              onClick={() => onAddSubmit(contactType, contactType === 1 ? addPhoneValue! : addValue)}
            />
            <Button
              label={t('common.cancel')}
              variant="outlined"
              styleClass={classes.cancelBtn}
              testId={`${testIdPrefix}-add-cancel-button`}
              onClick={onAddCancel}
            />
          </Box>
        </Box>
      )}

      {isMobile ? (
        <Box className={classes.mobileContactsList}>
          {primaryContact && renderMobileCard(primaryContact, true)}
          {secondaryContacts.map(contact => renderMobileCard(contact, false))}
          {!primaryContact && secondaryContacts.length === 0 && (
            <Typography className={classes.mobileEmptyText} data-testid={`${testIdPrefix}-empty-text`}>
              {emptyText}
            </Typography>
          )}
        </Box>
      ) : (
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell className={classes.tableHeaderCell}>{columnLabel}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">
                {t('myProfile.loginInformation.table.statusColumn')}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} align="right">
                {t('myProfile.loginInformation.table.actionsColumn')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {primaryContact && (
              <TableRow
                className={`${classes.tableDataRow} ${classes.tableRowPrimary}`}
                data-testid={`${testIdPrefix}-primary-row`}
              >
                <TableCell className={classes.emailCell} data-testid={`${testIdPrefix}-value-${primaryContact.id}`}>
                  {primaryContact.contactValue}
                </TableCell>
                <TableCell className={classes.statusCell} align="center">
                  <Chip label={t('myProfile.loginInformation.table.primaryBadge')} className={classes.primaryBadge} />
                </TableCell>
                <TableCell className={classes.actionsCell} />
              </TableRow>
            )}
            {secondaryContacts.map(contact => (
              <TableRow
                key={contact.id}
                className={`${classes.tableDataRow} ${classes.tableRowSecondary}`}
                data-testid={`${testIdPrefix}-row-${contact.id}`}
              >
                <TableCell className={classes.emailCell} data-testid={`${testIdPrefix}-value-${contact.id}`}>
                  {contact.contactValue}
                </TableCell>
                <TableCell className={classes.statusCell} align="center">
                  <Chip
                    label={t('myProfile.loginInformation.table.secondaryBadge')}
                    className={classes.secondaryBadge}
                  />
                </TableCell>
                <TableCell className={classes.actionsCell}>
                  <Box className={classes.actionsCellInner}>
                    {contact.isVerified ? (
                      <button
                        className={classes.setPrimaryLink}
                        data-testid={`${testIdPrefix}-make-primary-button-${contact.id}`}
                        onClick={() => onMakePrimary(contact)}
                      >
                        {t('myProfile.loginInformation.table.makePrimary')}
                      </button>
                    ) : (
                      <button
                        className={classes.setPrimaryLink}
                        data-testid={`${testIdPrefix}-verify-button-${contact.id}`}
                        onClick={() => onVerify(contact)}
                      >
                        {t('myProfile.loginInformation.table.verify')}
                      </button>
                    )}
                    <button
                      className={classes.removeLink}
                      data-testid={`${testIdPrefix}-remove-button-${contact.id}`}
                      onClick={() => onRemove(contact)}
                    >
                      {t('myProfile.loginInformation.table.remove')}
                    </button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!primaryContact && secondaryContacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className={classes.emptyText} data-testid={`${testIdPrefix}-empty-text`}>
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Box className={classes.noteBox}>
        <span className={classes.noteLabel}>Note: </span>
        <span className={classes.noteBody}>{noteText}</span>
      </Box>
    </Box>
  );
};

interface LoginInformationProps {
  isAdminSessionContext?: boolean;
}

export const LoginInformation: React.FC<LoginInformationProps> = ({ isAdminSessionContext }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { emails, phones, workerId, languageCode, isAdmin } = useAppSelector(state => ({
    emails: (isAdminSessionContext ? state.workerData.adminContacts?.emails : state.workerData.contacts?.emails) ?? [],
    phones: (isAdminSessionContext ? state.workerData.adminContacts?.phones : state.workerData.contacts?.phones) ?? [],
    workerId: state.onboarding.id,
    languageCode: state.app?.languageCode,
    isAdmin: state.user?.isAdmin,
  }));

  const [isContactsLoading, setIsContactsLoading] = useState(emails.length === 0 && phones.length === 0);

  const [passwordModal, setPasswordModal] = useState<IPasswordModalState>({
    open: false,
    contact: null,
    action: 'makePrimary',
  });
  const [removeModal, setRemoveModal] = useState<IRemoveModalState>({ open: false, contact: null });
  const [verificationSentModal, setVerificationSentModal] = useState<IVerificationSentModalState>({
    open: false,
    contact: null,
  });

  const [passwordModalLoading, setPasswordModalLoading] = useState(false);
  const [passwordModalError, setPasswordModalError] = useState('');
  const [removeModalLoading, setRemoveModalLoading] = useState(false);
  const [removeModalError, setRemoveModalError] = useState('');

  const [showAddEmail, setShowAddEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailError, setNewEmailError] = useState('');
  const [showAddMobile, setShowAddMobile] = useState(false);
  // inputValue is what MobileInput displays; newMobile is the full E.164 for submission
  const [newMobileInputValue, setNewMobileInputValue] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [newMobileError, setNewMobileError] = useState('');

  const classes = useStyles();

  const refreshContacts = useCallback(
    async (showLoading = false) => {
      if (showLoading) setIsContactsLoading(true);
      await Promise.all([
        dispatch(fetchEmailContacts({ isAdminSessionContext })),
        dispatch(fetchPhoneContacts({ isAdminSessionContext })),
      ]);
      setIsContactsLoading(false);
    },
    [dispatch, isAdminSessionContext],
  );

  const refreshWorkerProfile = () => {
    if (workerId) {
      dispatch(fetchWorkerDetails({ workerId, languageCode }));
    }
  };

  useEffect(() => {
    refreshContacts(emails.length === 0 && phones.length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminSessionContext]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) refreshContacts();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleAddClick = (contactType: 0 | 1, contactValue: string) => {
    if (contactType === 0) {
      if (!contactValue.trim()) {
        setNewEmailError(t('myProfile.loginInformation.errors.emailRequired'));
        return;
      }
      if (!EMAIL_REGEX.test(contactValue.trim())) {
        setNewEmailError(t('myProfile.loginInformation.errors.invalidEmail'));
        return;
      }
      setNewEmailError('');
    } else {
      if (!contactValue.trim()) {
        setNewMobileError(t('myProfile.loginInformation.errors.mobileRequired'));
        return;
      }
      if (!isValidMobileNumber(contactValue.trim())) {
        setNewMobileError(t('myProfile.loginInformation.errors.invalidMobile'));
        return;
      }
      setNewMobileError('');
    }
    setPasswordModalError('');
    setPasswordModal({
      open: true,
      contact: null,
      action: 'add',
      addContactValue: contactValue.trim(),
      addContactType: contactType,
    });
  };

  const openRemoveModal = (contact: IContact) => {
    setRemoveModalError('');
    setRemoveModal({ open: true, contact });
  };

  const closeRemoveModal = () => {
    setRemoveModal({ open: false, contact: null });
    setRemoveModalError('');
  };

  const handleRemoveConfirm = async (password: string) => {
    if (!removeModal.contact) return;
    setRemoveModalLoading(true);
    setRemoveModalError('');
    try {
      await dispatch(
        removeContact({
          contactId: removeModal.contact.id,
          contactType: removeModal.contact.contactType,
          password,
          isAdminSessionContext,
        }),
      ).unwrap();
      closeRemoveModal();
      enqueueSnackbar(t('myProfile.loginInformation.toasts.contactRemoved'), { variant: 'success' });
      refreshWorkerProfile();
    } catch (error: any) {
      setRemoveModalError(typeof error === 'string' ? error : 'Something went wrong. Please try again.');
    } finally {
      setRemoveModalLoading(false);
    }
  };

  const openMakePrimaryModal = (contact: IContact) => {
    setPasswordModalError('');
    setPasswordModal({ open: true, contact, action: 'makePrimary' });
  };

  const openVerifyModal = (contact: IContact) => {
    setPasswordModalError('');
    setPasswordModal({ open: true, contact, action: 'verify' });
  };

  const closePasswordModal = () => {
    setPasswordModal(prev => ({ ...prev, open: false, contact: null }));
    setPasswordModalError('');
  };

  const handlePasswordSubmit = async (password: string) => {
    setPasswordModalLoading(true);
    setPasswordModalError('');
    try {
      if (passwordModal.action === 'add') {
        await dispatch(
          addContact({
            contactType: passwordModal.addContactType!,
            contactValue: passwordModal.addContactValue!,
            password,
            isAdminSessionContext,
          }),
        ).unwrap();
        closePasswordModal();
        if (passwordModal.addContactType === 0) {
          setShowAddEmail(false);
          setNewEmail('');
        } else {
          setShowAddMobile(false);
          setNewMobile('');
          setNewMobileInputValue('');
        }
        enqueueSnackbar(t('myProfile.loginInformation.toasts.contactAdded'), { variant: 'success' });
        refreshWorkerProfile();
      } else if (passwordModal.action === 'makePrimary') {
        await dispatch(
          makePrimaryContact({
            contactId: passwordModal.contact!.id,
            contactType: passwordModal.contact!.contactType,
            password,
            isAdminSessionContext,
          }),
        ).unwrap();
        closePasswordModal();
        enqueueSnackbar(t('myProfile.loginInformation.toasts.primaryUpdated'), { variant: 'success' });
        refreshWorkerProfile();
      } else {
        const contactForSent = passwordModal.contact;
        await dispatch(
          verifyContact({
            contactId: passwordModal.contact!.id,
            contactType: passwordModal.contact!.contactType,
            password,
            isAdminSessionContext,
          }),
        ).unwrap();
        closePasswordModal();
        setVerificationSentModal({ open: true, contact: contactForSent });
        refreshWorkerProfile();
      }
    } catch (error: any) {
      setPasswordModalError(typeof error === 'string' ? error : 'Something went wrong. Please try again.');
    } finally {
      setPasswordModalLoading(false);
    }
  };

  const closeVerificationSentModal = () => {
    setVerificationSentModal({ open: false, contact: null });
  };

  const handleVerifyNow = () => {
    refreshContacts();
    refreshWorkerProfile();
    closeVerificationSentModal();
  };

  const primaryEmail = emails.find(c => c.isPrimary) || null;
  const secondaryEmails = emails.filter(c => !c.isPrimary);
  const primaryPhone = phones.find(c => c.isPrimary) || null;
  const secondaryPhones = phones.filter(c => !c.isPrimary);

  if (isContactsLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
        data-testid="login-info-loader"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root} data-testid="login-info-container">
      <ContactSection
        contactType={0}
        primaryContact={primaryEmail}
        secondaryContacts={secondaryEmails}
        columnLabel={t('myProfile.loginInformation.email.columnLabel')}
        noteText={t('myProfile.loginInformation.email.note')}
        showAdd={showAddEmail}
        addValue={newEmail}
        addValueError={newEmailError}
        onAddOpen={() => setShowAddEmail(true)}
        onAddChange={v => {
          setNewEmail(v);
          setNewEmailError('');
        }}
        onAddCancel={() => {
          setShowAddEmail(false);
          setNewEmail('');
          setNewEmailError('');
        }}
        onAddSubmit={handleAddClick}
        onMakePrimary={openMakePrimaryModal}
        onVerify={openVerifyModal}
        onRemove={openRemoveModal}
      />

      <ContactSection
        contactType={1}
        primaryContact={primaryPhone}
        secondaryContacts={secondaryPhones}
        columnLabel={t('myProfile.loginInformation.mobile.columnLabel')}
        noteText={t('myProfile.loginInformation.mobile.note')}
        showAdd={showAddMobile}
        addValue={newMobileInputValue}
        addPhoneValue={newMobile}
        addValueError={newMobileError}
        onAddOpen={() => setShowAddMobile(true)}
        onAddChange={() => {}}
        onMobileChange={({ phone, inputValue }) => {
          setNewMobile(phone);
          setNewMobileInputValue(inputValue);
          setNewMobileError('');
        }}
        onAddCancel={() => {
          setShowAddMobile(false);
          setNewMobile('');
          setNewMobileInputValue('');
          setNewMobileError('');
        }}
        onAddSubmit={handleAddClick}
        onMakePrimary={openMakePrimaryModal}
        onVerify={openVerifyModal}
        onRemove={openRemoveModal}
      />

      <EnterPasswordModal
        isVisible={passwordModal.open}
        onClose={closePasswordModal}
        contact={passwordModal.contact}
        action={passwordModal.action}
        addContactValue={passwordModal.addContactValue}
        isLoading={passwordModalLoading}
        error={passwordModalError}
        isAdmin={isAdmin}
        onSubmit={handlePasswordSubmit}
      />

      <RemoveContactModal
        isVisible={removeModal.open}
        onClose={closeRemoveModal}
        contact={removeModal.contact}
        isLoading={removeModalLoading}
        error={removeModalError}
        isAdmin={isAdmin}
        onConfirm={handleRemoveConfirm}
      />

      <VerificationSentModal
        isVisible={verificationSentModal.open}
        onClose={closeVerificationSentModal}
        onVerifyNow={handleVerifyNow}
        contact={verificationSentModal.contact}
      />
    </Box>
  );
};
